import { Button, Form, Input, InputNumber, Select } from 'antd'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Modal } from '@/components'
import { Trans } from '@/components'
import { Metric } from '@/models/metric.ts'
interface AddMetricModalProps {
  onOk: (metric: Metric) => void
  onClose: () => void
  editingMetric?: Metric
}

type FormValuesType = {
  metricName: string
  description: string
  baselineType: number
  baselineValue: number
  retrieveAddress: string
}

export function AddMetricModal(props: AddMetricModalProps) {
  const [form] = Form.useForm<FormValuesType>()
  const { onOk, onClose, editingMetric } = props
  const { t } = useTranslation('scaling')
  useEffect(() => {
    if (editingMetric) {
      form.setFieldsValue({
        metricName: editingMetric.name,
        description: editingMetric.desc,
        baselineType: editingMetric.target.type,
        baselineValue: editingMetric.target.value,
        retrieveAddress: editingMetric.url,
      })
    } else {
      form.resetFields()
    }
  }, [editingMetric])

  const handleSubmit = (values: FormValuesType) => {
    onOk({
      type: 1,
      name: values.metricName,
      desc: values.description,
      target: {
        type: values.baselineType,
        value: values.baselineValue,
      },
      url: values.retrieveAddress,
    })
  }

  return (
    <Modal className="w-[640px]" open={true} onClose={onClose}>
      <Modal.Header>
        {editingMetric ? (
          <Trans i18nKey="createSubPage.addMetricModal.editTitle" ns="scaling" />
        ) : (
          <Trans i18nKey="createSubPage.addMetricModal.title" ns="scaling" />
        )}
      </Modal.Header>
      <Modal.Body>
        <Form layout="vertical" form={form} initialValues={{ baselineType: 3 }}>
          <Form.Item
            label={
              <div className="t-sb text-with-star">
                <Trans i18nKey="createSubPage.addMetricModal.form.metricName" ns="scaling" />
              </div>
            }
            name="metricName"
            rules={[
              {
                required: true,
                message: t('createSubPage.addMetricModal.form.metricNamePlaceholder'),
              },
            ]}
          >
            <Input
              className="h-11"
              placeholder={t('createSubPage.addMetricModal.form.metricNamePlaceholder')}
            />
          </Form.Item>
          <Form.Item
            label={
              <div className="t-sb text-with-star">
                <Trans i18nKey="createSubPage.addMetricModal.form.description" ns="scaling" />
              </div>
            }
            name="description"
            rules={[
              {
                required: true,
                message: t('createSubPage.addMetricModal.form.descriptionPlaceholder'),
              },
            ]}
          >
            <Input
              className="h-11"
              placeholder={t('createSubPage.addMetricModal.form.descriptionPlaceholder')}
            />
          </Form.Item>
          <div className="w-full flex gap-x-4">
            <Form.Item
              label={
                <div className="t-sb text-with-star">
                  <Trans i18nKey="createSubPage.addMetricModal.form.baselineType" ns="scaling" />
                </div>
              }
              name="baselineType"
              className="w-1/2"
              rules={[
                {
                  required: true,
                  message: t('createSubPage.addMetricModal.form.baselineTypePlaceholder'),
                },
              ]}
            >
              <Select className="h-11">
                <Select.Option value={3}>
                  <Trans
                    i18nKey="createSubPage.addMetricModal.form.baselineTypeNumeric"
                    ns="scaling"
                  />
                </Select.Option>
                <Select.Option value={1}>
                  <Trans
                    i18nKey="createSubPage.addMetricModal.form.baselineTypeRatio"
                    ns="scaling"
                  />
                </Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              label={
                <div className="t-sb text-with-star">
                  <Trans i18nKey="createSubPage.addMetricModal.form.baselineValue" ns="scaling" />
                </div>
              }
              name="baselineValue"
              className="w-1/2"
              rules={[
                {
                  required: true,
                  message: t('createSubPage.addMetricModal.form.baselineValuePlaceholder'),
                },
              ]}
            >
              <InputNumber className="w-full h-11 flex items-center" min={0} />
            </Form.Item>
          </div>
          <Form.Item
            label={
              <div className="t-sb text-with-star">
                <Trans i18nKey="createSubPage.addMetricModal.form.retrieveAddress" ns="scaling" />
              </div>
            }
            name="retrieveAddress"
            rules={[
              {
                required: true,
                type: 'url',
                message: t('createSubPage.addMetricModal.form.retrieveAddressPlaceholder'),
              },
            ]}
          >
            <Input
              className="h-11"
              placeholder={t('createSubPage.addMetricModal.form.retrieveAddressPlaceholder')}
            />
          </Form.Item>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button size="large" className="h-11 px-4 py-3 t-sb" onClick={onClose}>
          <Trans i18nKey="common.confirmModalCancel" />
        </Button>
        <Button
          type="primary"
          size="large"
          className="h-11 px-4 py-3 t-sb"
          onClick={() => {
            form.validateFields().then((values) => {
              handleSubmit(values)
            })
          }}
        >
          <Trans i18nKey="common.confirmModalOk">Confirm</Trans>
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
